import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import ecgMain from '../Images/ecg18.jpeg';
import ecg1 from '../Images/mrp6000.jpeg';
import ecg2 from '../Images/ecg60.jpeg';
import ecg3 from '../Images/ecg1.jpeg';
import heroImage from '../Images/ecgproduct.jpg';
import Footer from '../Pages/Footer';

const imageDetails = {
  [ecg2]: {
    category: 'ECG Machine',
    heading: '3 Channel ECG Machine',
    title: 'MT ECG 30',
    subtitle: '3-Channel Electrocardiograph System',
    description:
      'The MT ECG 30 is a compact 3-channel ECG machine designed for accurate cardiac monitoring, intelligent ECG analysis, and reliable portable operation.',
    sections: {
      'ECG Acquisition & Signal Processing': {
        icon: '📈',
        items: [
          'Sync collection for 12-lead ECG with digital signal processing technology',
          'High-quality ECG waveform for low power frequency, baseline, and EMG filters',
        ],
      },

      'Display & Monitoring': {
        icon: '🖥️',
        items: [
          'Displays 1/3/6/12-lead ECG on screen',
          'Shows print mode, sensitivity, paper speed, and filter state for comparison-based diagnosis',
        ],
      },

      'Printing & Recording Modes': {
        icon: '🖨️',
        items: [
          'Supports manual, auto 6×2, auto 3×4+1, auto 3×4, auto 2×6+1, and auto 2×6 printing modes',
          'Adjustable waveform length and timed print function',
        ],
      },

      'Auto Analysis & Diagnosis': {
        icon: '📊',
        items: [
          'Auto-analysis for routine ECG parameters',
          'Auto-diagnosis to reduce doctors’ workload',
        ],
      },

      'Memory & Data Management': {
        icon: '💾',
        items: [
          'Built-in memory allows easy review and case statistics by the doctor',
        ],
      },

      'Connectivity & Software': {
        icon: '🌐',
        items: [
          'Optional ECG-Sync software support',
          'USB2.0 connection to PC for real-time data analysis',
          'Supports report printing and uploading stored cases from the device',
        ],
      },

      'Power Supply & Portability': {
        icon: '🔋',
        items: [
          'Dual power support with AC/DC operation',
          'Built-in rechargeable lithium battery',
          'Ideal for home visits and portable use',
        ],
      },
    },
  },

  [ecg1]: {
    category: 'ECG Machine',
    heading: '6 Channel ECG Machine',
    title: 'MT ECG 60',
    subtitle: '6-Channel Electrocardiograph System',
    description:
      'The MT ECG 60 is an advanced 12-lead ECG machine featuring high-resolution display, intelligent ECG analysis, multiple recording modes, and reliable battery performance for accurate and efficient cardiac diagnostics.',
    sections: {
      'Display & User Interface': {
        icon: '',
        items: [
          'High-Resolution Display',
          '7" high-resolution color LCD',
          'Touch screen and function button operation for convenient use',
          'Display of 3/6/12-lead ECG waveform',
          'Shows print mode, sensitivity, paper speed, filter status, etc. on one screen for convenient diagnosis',
        ],
      },

      'ECG Acquisition & Signal Processing': {
        icon: '🖥️',
        items: [
          'ECG Collection',
          'Sync collection for 12-lead ECG',
          'Supports routine 12-lead and Cabrera-lead waveform display',
          'High-quality ECG waveform acquisition',
          'AC Filter',
          'DFT Filter',
          'EMG Filter',
          'Low-pass Filter',
        ],
      },

      'Recording & Printing Modes': {
        icon: '💾',
        items: [
          'Auto 1×12 Auto 2×6',
          'Auto 2×6+1 Auto 3×4',
          'Auto 3×4+1 Auto 4×3',
          'Auto 4×3+1 Auto 6×2',
          'Auto 6×2+1',
          'Rhythm 7',
          'Rhythm 6',
          'Rhythm 5',
          'Rhythm 3',
          'Rhythm 4',
          'Manual Mode',
          'Rhythm 2',
          'Manual Mode',
        ],
      },

      'Printing Features': {
        icon: '',
        items: [
          'Printed content includes time, calibration signal, filter status, and patient information',
          'Configurable waveform length',
          'Diagnosis information',
          'QRS overlap',
          'Histogram',
          'Trend chart',
          'Interval list',
          'Timed print support',
        ],
      },

      'Measurement & Interpretation': {
        icon: '📊',
        items: [
          'Auto-measurement and auto-interpretation for routine ECG parameters',
          'HR',
          'P-R Interval',
          'P Duration',
          'QRS Duration',
          'QT/QTc Interval',
          'P-R-T Axis',
          'R(V5)/S(V1)',
          'R(V5)+S(V1)',
          'Physician conclusion generation',
          'Reply measurement: rhythm lead or abnormal rhythm re-measurement',
        ],
      },

      'Power Supply & Battery': {
        icon: '🔋',
        items: [
          'AC/DC power supply',
          'Adapts to AC 50/60Hz',
          'Built-in rechargeable lithium battery',
          'Charging circuit with over-current and over-voltage protection',
          'Up to 10-hour standby in optimized DC mode',
          'Over 3-hour continuous printing',
          'Up to 1000 ECG waveforms (3s cases)',
          'Connectivity & language support',
          'Supports external printer connection',
          'Supports USB storage device connection',
        ],
      },
    },
  },

  [ecgMain]: {
    category: 'ECG Machine',
    heading: '18 Channel ECG Machine',
    title: 'MT ECG 180',
    subtitle: '18-Channel Electrocardiograph System',
    description:
      'The MT ECG 180 is a high-performance 18-lead electrocardiograph system designed for accurate ECG acquisition, seamless workflow, and intelligent management in hospitals, clinics, and diagnostic centres.',
    sections: {
      'Advanced ECG/18-Lead Performance': {
        icon: '❤️',
        items: [
          'Simultaneous 18-Lead ECG Acquisition',
          'Supports 18-Lead & Cabrera Lead Display',
          'High-Quality ECG Signal Processing',
          'Power Frequency Filter',
          'Baseline Filter',
          'EMG Filter',
          'Low-Pass Filter',
          'Auto & Manual ECG Recording Modes',
          'Automatic ECG Measurement & Interpretation',
          'Arrhythmia Detection & Rhythm Lead Selection',
        ],
      },

      'Smart Display & Operation': {
        icon: '🖥️',
        items: [
          '10.1” High-Resolution Color LCD',
          'Multi-Language Interface',
          'Touch Screen + Function Button Operation',
          '1280 × 800 Display Resolution',
          'Built-In Virtual Keyboard',
          'HR Value',
          'Battery Level',
          'Filter Status',
          'Sensitivity',
          'Measured Data & Interpretation',
          'Paper Speed',
        ],
      },

      'Intelligent Printing System': {
        icon: '🖨️',
        items: [
          'Built-in thermal printer',
          'Auto MxN, Auto MxN+1, MxN+2, MxN+3, Rhythm M line, and manual print modes',
          'Configurable waveform length, output parameters, diagnostic conclusion',
          'Auto arrhythmia print function and timed print function',
          'Printed content includes time, paper speed, sensitivity, patient information',
        ],
      },

      'Accurate Measurement & Diagnosis': {
        icon: '📊',
        items: [
          'Auto-measurement and auto-interpretation for routine ECG parameters',
          'Measures HR, PR Interval, P Duration, QRS Duration, T Duration, QT/QTc Interval',
          'P/QRS/T Axis, R(V5), S(V1), R(V5)+S(V1) amplitude, Cornell Index',
        ],
      },

      'Memory & Data Management': {
        icon: '💾',
        items: [
          'Built-in large-capacity memory stores at least 4000 medical records',
          'Review, inquire, modify, transmit, print and export historical records',
          'Export to dat, pdf, xml, bmp, jpeg, png formats',
          'LAN and USB cable transmission, auto upload cases and download reports',
        ],
      },

      'Connectivity & Communication': {
        icon: '📡',
        items: [
          'Wi-Fi wireless communication',
          'LAN and USB cable transmission',
          'Supports external USB keyboard, mouse, scanner, printer',
          ' Automatic Case Upload & Report Download',
        ],
      },

      'Power & Reliability': {
        icon: '🔋',
        items: [
          'AC/DC Power Supply Support',
          'Built-in rechargeable lithium battery with charging circuit',
          'Perfect battery overcurrent and overvoltage protection',
          'Up to 10-hour standby in optimal DC state',
          'Continuous print more than 3 hours',
          'Record up to 1000 ECG waveforms (3s case)',
          'Lead-Off & Lack-of-Paper Alerts',
        ],
      },
    },
  },

  [ecg3]: {
    category: 'ECG Machine',
    heading: '12 Channel ECG Machine',
    title: 'MT ECG 120',
    subtitle: '12-Channel Electrocardiograph System',
    description:
      'The MT ECG 120 is an advanced 12-channel ECG machine featuring HD touchscreen display, high-precision ECG measurement, and intelligent data management for efficient cardiac diagnostics.',
    sections: {
      'Display & User Interface': {
        icon: '🖥️',
        items: [
          '10.1-inch full fit color touch screen',
          'Resolution up to 1280 × 800',
          'Provides clearer waveforms, richer display content, and reliable results',
        ],
      },

      'Touch & Button Operation': {
        icon: '👆',
        items: [
          'Combination of touch screen and physical buttons for fast and intuitive operation',
          'Soft silicone buttons for comfortable touch and easy control',
        ],
      },

      'Data Management': {
        icon: '💾',
        items: [
          'Built-in large-capacity memory stores at least 4000 medical records',
          'Supports export in multiple electronic file formats',
          'Convenient and fast operation interface',
        ],
      },

      'Ergonomic Design': {
        icon: '🎯',
        items: [
          'Supports external USB keyboard, mouse, barcode scanner, printer, and ID card reader',
        ],
      },

      'External Device Connectivity': {
        icon: '🔌',
        items: [
          'Supports external USB keyboard, mouse, barcode scanner, printer, ID card reader, and other devices',
        ],
      },

      'High Precision Measurement': {
        icon: '📊',
        items: [
          '24-bit sampling accuracy',
          '32kHz sampling frequency',
          'Delivers accurate waveforms and reliable analysis results',
        ],
      },
    },
  },
};

const EcgDetail = () => {
  const allImages = [ecgMain, ecg1, ecg2, ecg3];

  const [mainImage, setMainImage] = useState(ecgMain);
  const [relatedImages, setRelatedImages] = useState(
    allImages.filter((img) => img !== ecgMain)
  );
  const [isMobile, setIsMobile] = useState(false);

  useLayoutEffect(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleImageClick = (clickedImage) => {
    setMainImage(clickedImage);
    setRelatedImages(allImages.filter((img) => img !== clickedImage));

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  };

  const detail = imageDetails[mainImage];
  const sections = detail?.sections || {};

  return (
    <>
      <Helmet>
        <title>ECG Machines in India | Mindron Meditech Pvt. Ltd.</title>
        <meta
          name="description"
          content="Explore ECG machines by Mindron Meditech including 3-channel, 6-channel, 12-channel, and 18-channel ECG systems for hospitals, clinics, diagnostic centers, cardiac monitoring, ECG analysis, and report printing."
        />
        <link
          rel="canonical"
          href="https://www.mindronmeditech.com/products/ecg"
        />
      </Helmet>

      <div
        style={{
          fontFamily: 'sans-serif',
          margin: 0,
          padding: 0,
          overflowX: 'hidden',
        }}
      >
        {/* HERO */}
        <div
          style={{
            height: isMobile ? '30vh' : '80vh',
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <img
            src={heroImage}
            alt="Hero"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />

          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top, rgba(0,20,60,0.5) 0%, transparent 60%)',
            }}
          />
        </div>

        {/* PRODUCT INTRO */}
        <div
          style={{
            display: 'flex',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: '40px',
            padding: isMobile ? '32px 6%' : '48px 6%',
            alignItems: 'flex-start',
          }}
        >
          {/* Left: text */}
          <div style={{ flex: 1, minWidth: '260px' }}>
            <div
              style={{
                display: 'inline-block',
                backgroundColor: '#e8f0fe',
                color: '#1a56db',
                fontSize: '13px',
                fontWeight: '600',
                padding: '4px 14px',
                borderRadius: '20px',
                marginBottom: '14px',
              }}
            >
              {detail?.category}
            </div>

            <h1
              style={{
                fontSize: isMobile ? '26px' : '38px',
                fontWeight: '800',
                color: '#0a1628',
                margin: '0 0 8px',
                lineHeight: 1.2,
              }}
            >
              {detail?.title}
            </h1>

            <h2
              style={{
                fontSize: isMobile ? '16px' : '20px',
                fontWeight: '500',
                color: '#444',
                margin: '0 0 20px',
              }}
            >
              {detail?.subtitle}
            </h2>

            <p
              style={{
                fontSize: '15px',
                color: '#555',
                lineHeight: '1.8',
                maxWidth: '520px',
              }}
            >
              {detail?.description}
            </p>
          </div>

          {/* Right: product image */}
          <div
            style={{
              flex: '0 0 auto',
              width: isMobile ? '100%' : '340px',
              background: '#f4f6fb',
              borderRadius: '14px',
              padding: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={mainImage}
              alt="ECG Device"
              style={{
                width: '100%',
                maxHeight: '260px',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </div>
        </div>

        {/* KEY FEATURES GRID */}
        <div
          style={{
            padding: isMobile ? '16px 6% 40px' : '16px 6% 56px',
          }}
        >
          <div
            style={{
              width: '50px',
              height: '5px',
              backgroundColor: '#cc0000',
              borderRadius: '2px',
              marginBottom: '12px',
            }}
          />

          <h2
            style={{
              fontSize: '22px',
              fontWeight: '800',
              color: '#0a1628',
              marginBottom: '28px',
            }}
          >
            Key Features
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '20px',
            }}
          >
            {Object.entries(sections).map(([sectionName, sectionData], idx) => (
              <div
                key={idx}
                style={{
                  background: '#f4f6fb',
                  borderRadius: '12px',
                  padding: '22px 20px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '14px',
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{sectionData.icon}</span>

                  <h3
                    style={{
                      fontSize: '15px',
                      fontWeight: '700',
                      color: '#0a1628',
                      margin: 0,
                    }}
                  >
                    {sectionName}
                  </h3>
                </div>

                <ul
                  style={{
                    margin: 0,
                    paddingLeft: '18px',
                    listStyle: 'disc',
                  }}
                >
                  {sectionData.items.map((item, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: '13px',
                        color: '#555',
                        lineHeight: '1.8',
                      }}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* RELATED PRODUCTS */}
        <div
          style={{
            padding: isMobile ? '0 6% 48px' : '0 6% 60px',
          }}
        >
          <div
            style={{
              width: '50px',
              height: '5px',
              backgroundColor: '#cc0000',
              borderRadius: '2px',
              marginBottom: '12px',
            }}
          />

          <h2
            style={{
              fontSize: '22px',
              fontWeight: '800',
              color: '#0a1628',
              marginBottom: '24px',
            }}
          >
            Related Products
          </h2>

          <div
            style={{
              display: 'flex',
              gap: '20px',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              paddingBottom: '4px',
            }}
          >
            {relatedImages.map((img, index) => (
              <div
                key={index}
                onClick={() => handleImageClick(img)}
                style={{
                  flex: '0 0 auto',
                  width: '220px',
                  background: '#f4f6fb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow =
                    '0 8px 20px rgba(0,0,0,0.13)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 2px 8px rgba(0,0,0,0.07)';
                }}
              >
                <div
                  style={{
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '150px',
                  }}
                >
                  <img
                    src={img}
                    alt={`Related ${index}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '120px',
                      objectFit: 'contain',
                    }}
                  />
                </div>

                <div
                  style={{
                    background: '#001f4d',
                    padding: '10px 14px',
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: '13px',
                      color: '#fff',
                      fontWeight: '600',
                    }}
                  >
                    {imageDetails[img]?.title || `Product ${index + 1}`}
                  </p>

                  <p
                    style={{
                      margin: '4px 0 0',
                      fontSize: '11px',
                      color: '#aac4ff',
                    }}
                  >
                    {imageDetails[img]?.category}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Footer />

        <style>{`
          @media (max-width: 768px) {
            .header-container {
              height: 30vh !important;
            }
          }

          *::-webkit-scrollbar {
            display: none;
          }

          * {
            scrollbar-width: none;
          }

          body {
            overflow-x: hidden;
          }
        `}</style>
      </div>
    </>
  );
};

export default EcgDetail;